import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type {
  MeetingTagNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { useActionPanelSearchParams } from '@/features/action-panel/use-action-panel-search-params';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { colors } from '@/theme/colors';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { Inline } from '@/ui/line/line';
import { Logo } from '@/ui/logo/logo';
import { SmallText } from '@/ui/typography/widgets';

import { useCommentTagNotificationEntityName } from '../hooks/use-comment-tag-notification-entity-name.query';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { timeAgo } from '../utils/time-ago';

export const MeetingTagNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { entityId, entityTagged, meetingId, taggerName } =
    notification.data as MeetingTagNotificationData;
  const isRead = Boolean(notification?.readAt);
  const entityName = useCommentTagNotificationEntityName({
    entityId,
    entityType: entityTagged,
  });
  const navigateTo = useNavigateTo();
  const { markAsRead } = useMarkNotificationAsRead();
  const { generateActionItemSearchParam } = useActionPanelSearchParams();

  const handleNavigateToMeeting = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    const meetingSearchParams = generateActionItemSearchParam(
      meetingId,
      'meeting',
    );

    if (entityTagged === 'company') {
      return navigateTo.company({ companyId: entityId }, meetingSearchParams);
    }

    if (entityTagged === 'project') {
      return navigateTo.project({ projectId: entityId }, meetingSearchParams);
    }
  };

  return (
    <Container
      isRead={isRead}
      justify="space-between"
      alignItems="center"
      onClick={handleNavigateToMeeting}
    >
      <Inline gap="12px" alignItems="center">
        <AuthorInitials
          name={taggerName ?? ''}
          bgColor={isRead ? colors.system.disable : colors.blue.primaryA}
        />
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="meetingSummaryTaggedYou"
            ns="notification"
            values={{
              entityName: entityName ?? '',
              taggerName: taggerName ?? '',
            }}
            components={{ strong: <Strong /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </Container>
  );
};

const Container = styled(Flexbox)<{ isRead: boolean }>`
  border-radius: 10px;
  border: 1px ${colors.gray.c3} solid;
  padding: 16px;
  background-color: ${({ isRead }) =>
    isRead ? colors.blue.c9 : colors.basics.white};
  cursor: pointer;
`;

const AuthorInitials = styled(Logo)`
  font-weight: 600;
`;

const Strong = styled('span')`
  font-weight: 600;
  color: ${colors.basics.black};
`;
